import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { DashboardLayoutSkeleton } from "@/components/DashboardLayoutSkeleton";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { trackEvent } from "@/lib/analytics";
import { trpc } from "@/lib/trpc";
import { ArrowRight, Key, Loader2, Network, Plus, Settings, Trash2, Zap } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";

export default function ProxyPass() {
  const { user, loading: authLoading } = useAuth();
  const [, navigate] = useLocation();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newProxy, setNewProxy] = useState({
    name: "",
    description: "",
    secretId: 0,
    targetUrl: "",
    authType: "bearer" as "bearer" | "basic" | "api_key" | "custom",
    authHeaderName: "Authorization",
    authPrefix: "Bearer",
  });

  const { data: proxies, isLoading: proxiesLoading, refetch } = trpc.proxy.list.useQuery();
  const { data: secrets } = trpc.secrets.list.useQuery();
  const createProxy = trpc.proxy.create.useMutation({
    onSuccess: () => {
      trackEvent("proxy_created");
      toast.success("Proxy configuration created");
      setIsCreateOpen(false);
      setNewProxy({
        name: "",
        description: "",
        secretId: 0,
        targetUrl: "",
        authType: "bearer",
        authHeaderName: "Authorization",
        authPrefix: "Bearer",
      });
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const deleteProxy = trpc.proxy.delete.useMutation({
    onSuccess: () => {
      trackEvent("proxy_deleted");
      toast.success("Proxy configuration deleted");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  if (authLoading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-muted-foreground">Please sign in to manage proxy configurations.</p>
        </div>
      </DashboardLayout>
    );
  }

  const handleCreate = () => {
    if (!newProxy.name || !newProxy.secretId || !newProxy.targetUrl) {
      toast.error("Please fill in all required fields");
      return;
    }
    createProxy.mutate(newProxy);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader
          title="Proxy Pass"
          subtitle="Forward API requests with automatic credential injection"
          action={
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New Proxy
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Create Proxy Configuration</DialogTitle>
                  <DialogDescription>
                    Set up a proxy that forwards requests to a target API with your credentials.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      placeholder="OpenAI Proxy"
                      value={newProxy.name}
                      onChange={(e) => setNewProxy({ ...newProxy, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Proxy for OpenAI API calls"
                      value={newProxy.description}
                      onChange={(e) => setNewProxy({ ...newProxy, description: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="secret">API Key (Secret) *</Label>
                    <Select
                      value={newProxy.secretId ? String(newProxy.secretId) : ""}
                      onValueChange={(value) => setNewProxy({ ...newProxy, secretId: parseInt(value) })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a secret" />
                      </SelectTrigger>
                      <SelectContent>
                        {secrets?.map((secret) => (
                          <SelectItem key={secret.id} value={String(secret.id)}>
                            {secret.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="targetUrl">Target API URL *</Label>
                    <Input
                      id="targetUrl"
                      placeholder="https://api.openai.com"
                      value={newProxy.targetUrl}
                      onChange={(e) => setNewProxy({ ...newProxy, targetUrl: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="authType">Authentication Type</Label>
                    <Select
                      value={newProxy.authType}
                      onValueChange={(value: "bearer" | "basic" | "api_key" | "custom") => {
                        const defaults: Record<string, { header: string; prefix: string }> = {
                          bearer: { header: "Authorization", prefix: "Bearer" },
                          basic: { header: "Authorization", prefix: "Basic" },
                          api_key: { header: "X-API-Key", prefix: "" },
                          custom: { header: "Authorization", prefix: "" },
                        };
                        setNewProxy({
                          ...newProxy,
                          authType: value,
                          authHeaderName: defaults[value].header,
                          authPrefix: defaults[value].prefix,
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bearer">Bearer Token</SelectItem>
                        <SelectItem value="basic">Basic Auth</SelectItem>
                        <SelectItem value="api_key">API Key Header</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newProxy.authType === "custom" && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="authHeaderName">Header Name</Label>
                        <Input
                          id="authHeaderName"
                          placeholder="Authorization"
                          value={newProxy.authHeaderName}
                          onChange={(e) => setNewProxy({ ...newProxy, authHeaderName: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="authPrefix">Header Prefix</Label>
                        <Input
                          id="authPrefix"
                          placeholder="Bearer "
                          value={newProxy.authPrefix}
                          onChange={(e) => setNewProxy({ ...newProxy, authPrefix: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreate} disabled={createProxy.isPending}>
                    {createProxy.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Create Proxy
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          }
        />

        {/* How it works */}
        <Card className="bg-card/50 border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Network className="h-5 w-5 text-primary" />
              How Proxy Pass Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-4">
              <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                <span className="font-mono">Client</span>
              </div>
              <ArrowRight className="h-4 w-4" />
              <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 border border-primary/20 rounded-lg">
                <span className="font-mono text-primary">Temp Token</span>
              </div>
              <ArrowRight className="h-4 w-4" />
              <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                <span className="font-mono">OO Proxy</span>
              </div>
              <ArrowRight className="h-4 w-4" />
              <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                <span className="font-mono text-green-600">Real API Key</span>
              </div>
              <ArrowRight className="h-4 w-4" />
              <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                <span className="font-mono">Target API</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Proxy list */}
        {proxiesLoading ? (
          <div className="flex justify-center py-12">
            <Spinner className="size-8 text-primary" />
          </div>
        ) : proxies && proxies.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {proxies.map((proxy) => (
              <Card
                key={proxy.id}
                className="cursor-pointer hover:border-primary/50 transition-colors group"
                onClick={() => navigate(`/proxy/${proxy.id}`)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Zap className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{proxy.name}</CardTitle>
                        <CardDescription className="text-xs truncate max-w-[200px]">
                          {proxy.targetUrl}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant={proxy.isActive ? "default" : "secondary"}>
                      {proxy.isActive ? "Active" : "Disabled"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Key className="h-3.5 w-3.5" />
                      <span>{proxy.authType}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Zap className="h-3.5 w-3.5" />
                      <span>{proxy.usageCount} calls</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-end mt-3 gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/proxy/${proxy.id}`);
                      }}
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm("Delete this proxy configuration?")) {
                          deleteProxy.mutate({ id: proxy.id });
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Network />
              </EmptyMedia>
              <EmptyTitle>No Proxy Configurations</EmptyTitle>
              <EmptyDescription>
                Create a proxy configuration to forward API requests with automatic credential injection.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button onClick={() => setIsCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Proxy
              </Button>
            </EmptyContent>
          </Empty>
        )}
      </div>
    </DashboardLayout>
  );
}
